/**
 * Filo cart-drawer upsell — horizontal cards, arrow slider, in-drawer size panel.
 */
(() => {
  /** @type {Map<string, ReturnType<typeof normalizeProduct>>} */
  const productStore = new Map();

  const ROOT = () => window.Shopify?.routes?.root || '/';
  const currency = () => window.Shopify?.currency?.active || 'INR';

  const formatMoney = (cents) => {
    const amount = (Number(cents) || 0) / 100;
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currency(),
        minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
        maximumFractionDigits: 2,
      }).format(amount);
    } catch {
      return String(amount);
    }
  };

  const escapeHtml = (value) =>
    String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');

  /** @param {string} path */
  const url = (path) => {
    const root = ROOT();
    const clean = String(path || '').replace(/^\//, '');
    return root.endsWith('/') ? `${root}${clean}` : `${root}/${clean}`;
  };

  const imageUrl = (src, width = 240) => {
    if (!src) return '';
    const base = src.startsWith('//') ? `https:${src}` : src;
    return base.includes('?') ? `${base}&width=${width}` : `${base}?width=${width}`;
  };

  const toCents = (raw) => {
    if (raw == null) return 0;
    if (typeof raw === 'string') return Math.round(parseFloat(raw) * 100) || 0;
    if (typeof raw === 'number' && !Number.isInteger(raw)) return Math.round(raw * 100);
    return Number(raw) || 0;
  };

  async function fetchRecommendations(productId, intent, limit = 8) {
    if (!productId) return [];
    try {
      const res = await fetch(
        url(`recommendations/products.json?product_id=${productId}&limit=${limit}&intent=${intent}`)
      );
      if (!res.ok) return [];
      const data = await res.json();
      return Array.isArray(data.products) ? data.products : [];
    } catch {
      return [];
    }
  }

  async function fetchCatalogFallback(limit = 10) {
    const handles = ['all', 'best-sellers', 'explore-more', 'new-arrivals'];
    for (const handle of handles) {
      try {
        const res = await fetch(url(`collections/${handle}/products.json?limit=${limit}`));
        if (!res.ok) continue;
        const data = await res.json();
        if (Array.isArray(data.products) && data.products.length) return data.products;
      } catch {
        /* try the next collection */
      }
    }
    return [];
  }

  /** @param {string} handle */
  async function fetchProductJs(handle) {
    try {
      const res = await fetch(url(`products/${handle}.js`));
      if (!res.ok) return null;
      return await res.json();
    } catch {
      return null;
    }
  }

  /** @param {Record<string, any>} product */
  function normalizeProduct(product) {
    const variants = Array.isArray(product.variants) ? product.variants : [];
    const available = variants.filter((v) => v.available !== false);

    let image = '';
    if (typeof product.featured_image === 'string') image = product.featured_image;
    else if (product.featured_image?.src) image = product.featured_image.src;
    else if (typeof product.images?.[0] === 'string') image = product.images[0];
    else if (product.images?.[0]?.src) image = product.images[0].src;
    else if (product.media?.[0]?.preview_image?.src) image = product.media[0].preview_image.src;

    const priceCents = toCents(product.price_min ?? product.price ?? available[0]?.price ?? 0);
    const compareRaw =
      product.compare_at_price_min ?? product.compare_at_price ?? available[0]?.compare_at_price ?? 0;
    const compareCents = toCents(compareRaw);

    const options = Array.isArray(product.options)
      ? product.options.map((opt) => (typeof opt === 'string' ? opt : opt?.name || 'Option'))
      : [];

    return {
      id: product.id,
      handle: product.handle,
      title: product.title,
      url: product.url || url(`products/${product.handle}`),
      image,
      priceCents,
      compareCents: compareCents > priceCents ? compareCents : 0,
      variants,
      available,
      options,
    };
  }

  function sizeLabel(variant) {
    if (!variant?.title || variant.title === 'Default Title') return 'One size';
    const parts = String(variant.title).split(' / ');
    return parts[parts.length - 1] || variant.title;
  }

  function isSingleAdd(product) {
    if (!product?.available?.length) return false;
    if (product.available.length === 1) {
      const v = product.available[0];
      return !v.title || v.title === 'Default Title' || product.variants.length === 1;
    }
    return false;
  }

  /* ── In-drawer size panel ──────────────────────────────────────────────── */

  /** @type {HTMLElement | null} */
  let panelEl = null;
  /** @type {string | number | null} */
  let selectedVariantId = null;

  function getDrawerHost() {
    return (
      document.querySelector('#cart-drawer dialog[open] .cart-drawer__inner') ||
      document.querySelector('theme-drawer dialog[open] .cart-drawer__inner') ||
      document.querySelector('.cart-drawer__inner')
    );
  }

  function ensurePanel() {
    const host = getDrawerHost();
    if (!(host instanceof HTMLElement)) return null;
    if (panelEl && host.contains(panelEl)) return panelEl;

    panelEl = document.createElement('div');
    panelEl.className = 'filo-upsell-panel';
    panelEl.hidden = true;
    panelEl.setAttribute('aria-hidden', 'true');
    panelEl.innerHTML = `
      <div class="filo-upsell-panel__inner" role="dialog" aria-modal="true" aria-labelledby="filo-upsell-panel-title">
        <div class="filo-upsell-panel__bar">
          <button type="button" class="filo-upsell-panel__back" data-filo-upsell-close aria-label="Back">
            <span aria-hidden="true">←</span> Back
          </button>
        </div>
        <div class="filo-upsell-panel__product">
          <div class="filo-upsell-panel__media" data-filo-upsell-media></div>
          <div class="filo-upsell-panel__info">
            <p class="filo-upsell-panel__title" id="filo-upsell-panel-title" data-filo-upsell-title></p>
            <p class="filo-upsell-panel__price" data-filo-upsell-price></p>
          </div>
        </div>
        <p class="filo-upsell-panel__label" data-filo-upsell-label>Select size</p>
        <div class="filo-upsell-panel__sizes" data-filo-upsell-sizes role="listbox" aria-label="Sizes"></div>
        <p class="filo-upsell-panel__error" data-filo-upsell-error hidden></p>
        <button type="button" class="filo-upsell-panel__confirm" data-filo-upsell-confirm disabled>
          Add to bag
        </button>
      </div>
    `;

    if (getComputedStyle(host).position === 'static') host.style.position = 'relative';
    host.appendChild(panelEl);

    panelEl.addEventListener('click', (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (target.closest('[data-filo-upsell-close]')) {
        closePanel();
        return;
      }
      const sizeBtn = target.closest('[data-filo-upsell-size]');
      if (sizeBtn instanceof HTMLButtonElement) {
        selectedVariantId = sizeBtn.dataset.variantId || null;
        panelEl
          ?.querySelectorAll('[data-filo-upsell-size]')
          .forEach((btn) => btn.setAttribute('aria-selected', btn === sizeBtn ? 'true' : 'false'));
        const confirm = panelEl?.querySelector('[data-filo-upsell-confirm]');
        if (confirm instanceof HTMLButtonElement) confirm.disabled = !selectedVariantId;
        const err = panelEl?.querySelector('[data-filo-upsell-error]');
        if (err instanceof HTMLElement) err.hidden = true;
        return;
      }
      if (target.closest('[data-filo-upsell-confirm]')) {
        const confirm = panelEl?.querySelector('[data-filo-upsell-confirm]');
        if (confirm instanceof HTMLButtonElement && selectedVariantId) {
          addVariant(selectedVariantId, confirm).then((ok) => {
            if (ok) closePanel();
          });
        }
      }
    });

    return panelEl;
  }

  function closePanel() {
    if (!panelEl) return;
    panelEl.classList.remove('is-open');
    panelEl.hidden = true;
    panelEl.setAttribute('aria-hidden', 'true');
    selectedVariantId = null;
  }

  /** @param {ReturnType<typeof normalizeProduct>} product */
  async function openPanel(product) {
    const panel = ensurePanel();
    if (!panel) return;

    selectedVariantId = null;
    const fresh = await fetchProductJs(product.handle);
    if (fresh) {
      product = normalizeProduct(fresh);
      productStore.set(product.handle, product);
    }
    if (!product.available.length) return;

    const media = panel.querySelector('[data-filo-upsell-media]');
    const title = panel.querySelector('[data-filo-upsell-title]');
    const price = panel.querySelector('[data-filo-upsell-price]');
    const label = panel.querySelector('[data-filo-upsell-label]');
    const sizes = panel.querySelector('[data-filo-upsell-sizes]');
    const error = panel.querySelector('[data-filo-upsell-error]');
    const confirm = panel.querySelector('[data-filo-upsell-confirm]');

    if (media instanceof HTMLElement) {
      media.innerHTML = product.image
        ? `<img src="${escapeHtml(imageUrl(product.image, 240))}" alt="" width="72" height="72">`
        : '';
    }
    if (title) title.textContent = product.title;
    if (price) price.textContent = formatMoney(product.priceCents);

    const sizeOption =
      product.options.find((name) => /size/i.test(name)) ||
      product.options[product.options.length - 1] ||
      'Size';
    if (label) label.textContent = `Select ${sizeOption.toLowerCase()}`;

    if (sizes instanceof HTMLElement) {
      sizes.innerHTML = product.available
        .map(
          (v) => `
          <button type="button" class="filo-upsell-panel__size" data-filo-upsell-size
            data-variant-id="${v.id}" role="option" aria-selected="false">
            ${escapeHtml(sizeLabel(v))}
          </button>`
        )
        .join('');
    }

    if (error instanceof HTMLElement) {
      error.hidden = true;
      error.textContent = '';
    }
    if (confirm instanceof HTMLButtonElement) {
      confirm.disabled = true;
      confirm.textContent = 'Add to bag';
      confirm.dataset.busy = '0';
    }

    panel.hidden = false;
    panel.setAttribute('aria-hidden', 'false');
    requestAnimationFrame(() => panel.classList.add('is-open'));
  }

  const themeCardCache = new Map();
  let themeCardSectionId = '';

  async function themeCardSection(handle) {
    if (themeCardSectionId) return themeCardSectionId;
    const res = await fetch(url(`products/${handle}?view=horizon-card`));
    if (!res.ok) return '';
    const text = await res.text();
    const match = text.match(/id="shopify-section-(template--\d+__card)"/);
    themeCardSectionId = match ? match[1] : '';
    return themeCardSectionId;
  }

  async function themeCardHtml(handle) {
    if (themeCardCache.has(handle)) return themeCardCache.get(handle);
    try {
      const sectionId = await themeCardSection(handle);
      if (!sectionId) throw new Error('section');
      const res = await fetch(url(`products/${handle}?view=horizon-card&section_id=${sectionId}`));
      if (!res.ok) throw new Error('card');
      const raw = await res.text();
      if (!raw.includes('product-card')) throw new Error('empty');
      const doc = new DOMParser().parseFromString(raw, 'text/html');
      doc.querySelectorAll('link[rel="stylesheet"]').forEach((node) => {
        if (!node.href || document.head.querySelector(`link[href="${node.href}"]`)) return;
        document.head.appendChild(node.cloneNode(true));
      });
      doc.querySelectorAll('style').forEach((node) => {
        const text = node.textContent || '';
        if (!text || [...document.head.querySelectorAll('style')].some((style) => style.textContent === text)) return;
        document.head.appendChild(node.cloneNode(true));
      });
      const card = doc.querySelector('product-card, .product-card');
      const html = card ? card.outerHTML : '';
      themeCardCache.set(handle, html);
      return html;
    } catch {
      themeCardCache.set(handle, '');
      return '';
    }
  }

  /**
   * @param {HTMLElement} root
   * @param {ReturnType<typeof normalizeProduct>[]} products
   */
  async function render(root, products) {
    const track = root.querySelector('[data-filo-cart-upsell-track]');
    const nav = root.querySelector('[data-filo-cart-upsell-nav]');
    if (!track) return;

    if (!products.length) {
      root.hidden = true;
      track.innerHTML = '';
      if (nav instanceof HTMLElement) nav.hidden = true;
      return;
    }

    const isEmpty = root.dataset.empty === 'true';
    const titleEl = root.querySelector('[data-filo-cart-upsell-title]');
    if (titleEl) titleEl.textContent = isEmpty ? 'You may also like' : 'Complete the look';
    const subEl = root.querySelector('[data-filo-cart-upsell-sub]');
    if (subEl instanceof HTMLElement) {
      subEl.hidden = !isEmpty;
    }

    const cards = await Promise.all(products.map((product) => themeCardHtml(product.handle)));
    track.innerHTML = products
      .map((product, index) => {
        productStore.set(product.handle, product);
        const card = cards[index];
        if (!card) return '';
        return `
          <div class="filo-cart-upsell__card" role="listitem" data-handle="${escapeHtml(product.handle)}">
            ${card}
          </div>
        `;
      })
      .filter(Boolean)
      .join('');

    root.hidden = track.innerHTML.trim() === '';
    if (nav instanceof HTMLElement) nav.hidden = track.children.length < 2;
    bindNav(root);
  }

  /** @param {HTMLElement} root */
  function bindNav(root) {
    const track = root.querySelector('[data-filo-cart-upsell-track]');
    if (!(track instanceof HTMLElement)) return;

    const prev = root.querySelector('[data-filo-upsell-prev]');
    const next = root.querySelector('[data-filo-upsell-next]');

    const scrollByCard = (dir) => {
      const card = track.querySelector('.filo-cart-upsell__card');
      const gap = Number.parseFloat(getComputedStyle(track).gap) || 10;
      const step = card instanceof HTMLElement ? card.offsetWidth + gap : track.clientWidth * 0.85;
      track.scrollBy({ left: dir * step, behavior: 'smooth' });
    };

    if (prev instanceof HTMLButtonElement) {
      prev.onclick = () => scrollByCard(-1);
    }
    if (next instanceof HTMLButtonElement) {
      next.onclick = () => scrollByCard(1);
    }
  }

  /** @param {HTMLElement} root */
  async function load(root) {
    if (root.dataset.loading === '1') return;
    root.dataset.loading = '1';

    const productId = root.dataset.productId?.trim();
    const exclude = new Set(
      String(root.dataset.exclude || '')
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean)
    );

    let products = [];
    if (productId) {
      products = await fetchRecommendations(productId, 'complementary', 10);
      if (!products.length) products = await fetchRecommendations(productId, 'related', 10);
    }
    if (!products.length) products = await fetchCatalogFallback(12);

    const list = products
      .map(normalizeProduct)
      .filter((p) => p.id && p.handle && !exclude.has(String(p.id)))
      .filter((p) => p.available.length > 0)
      .slice(0, 8);

    await render(root, list);
    root.dataset.loading = '0';
    root.dataset.ready = '1';
  }

  function cartSectionIds() {
    /** @type {Set<string>} */
    const ids = new Set(['cart-drawer-section']);
    document.querySelectorAll('cart-items-component[data-section-id]').forEach((el) => {
      const id = el.getAttribute('data-section-id');
      if (id) ids.add(id);
    });
    const header = document.querySelector('cart-icon')?.closest('[id^="shopify-section-"]');
    if (header?.id) ids.add(header.id.replace(/^shopify-section-/, ''));
    return Array.from(ids);
  }

  /** Horizon does not morph the drawer from a plain cart:lines-update event. */
  function applySections(sections) {
    if (!sections) return;
    const wasOpen = Boolean(document.querySelector('#cart-drawer dialog')?.open);
    for (const [id, html] of Object.entries(sections)) {
      if (typeof html !== 'string' || !html) continue;
      const el = document.getElementById(`shopify-section-${id}`);
      if (el) el.innerHTML = html;
    }
    if (!wasOpen) return;
    requestAnimationFrame(() => {
      const drawer = document.querySelector('#cart-drawer');
      if (typeof drawer?.open === 'function') drawer.open();
    });
  }

  /**
   * @param {string|number} variantId
   * @param {HTMLButtonElement} button
   * @returns {Promise<boolean>}
   */
  async function addVariant(variantId, button) {
    if (!variantId || button.dataset.busy === '1') return false;
    button.dataset.busy = '1';
    const original = button.innerHTML;
    button.disabled = true;
    if (button.classList.contains('filo-cart-upsell__add')) {
      button.innerHTML = '<span>…</span>';
    } else {
      button.textContent = 'Adding…';
    }

    const error = panelEl?.querySelector('[data-filo-upsell-error]');

    try {
      const res = await fetch(url('cart/add.js'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity: 1 }],
          sections: cartSectionIds().join(','),
          sections_url: window.location.pathname,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.description || data?.message || 'Unable to add');

      const linesUpdate = new CustomEvent('cart:lines-update', { bubbles: true });
      linesUpdate.action = 'add';
      linesUpdate.promise = Promise.resolve({
        detail: {
          sections: data.sections || {},
          itemCount: data.item_count,
          source: 'lithos-cart-upsell',
          didError: false,
        },
      });
      document.dispatchEvent(linesUpdate);
      applySections(data.sections);

      if (button.classList.contains('filo-cart-upsell__add')) {
        button.innerHTML = '<span>Added</span>';
      } else {
        button.textContent = 'Added';
      }
      setTimeout(() => {
        button.innerHTML = original;
        button.dataset.busy = '0';
        button.disabled = false;
      }, 700);
      return true;
    } catch (err) {
      console.error('[filo-cart-upsell]', err);
      if (error instanceof HTMLElement) {
        error.hidden = false;
        error.textContent = err instanceof Error ? err.message : 'Unable to add';
      }
      button.innerHTML = original;
      button.dataset.busy = '0';
      button.disabled = false;
      return false;
    }
  }

  function bindClicks() {
    document.addEventListener('click', async (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const button = target.closest('[data-filo-upsell-add]');
      if (!(button instanceof HTMLButtonElement)) return;
      if (!button.closest('[data-filo-cart-upsell]')) return;
      event.preventDefault();
      event.stopPropagation();

      const handle = button.dataset.handle;
      if (!handle) return;

      let product = productStore.get(handle);
      if (!product) {
        const fresh = await fetchProductJs(handle);
        if (fresh) {
          product = normalizeProduct(fresh);
          productStore.set(handle, product);
        }
      }
      if (!product?.available?.length) return;

      if (isSingleAdd(product)) {
        await addVariant(product.available[0].id, button);
        return;
      }
      openPanel(product);
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && panelEl && !panelEl.hidden) {
        event.stopPropagation();
        closePanel();
      }
    });
  }

  function initMounts() {
    document.querySelectorAll('[data-filo-cart-upsell]').forEach((el) => {
      if (!(el instanceof HTMLElement)) return;
      if (el.dataset.ready === '1' || el.dataset.loading === '1') return;
      load(el);
    });
  }

  function watchDrawer() {
    const refreshMounts = () => {
      closePanel();
      requestAnimationFrame(() => {
        document.querySelectorAll('[data-filo-cart-upsell]').forEach((el) => {
          if (el instanceof HTMLElement) {
            delete el.dataset.ready;
            delete el.dataset.loading;
          }
        });
        initMounts();
      });
    };
    document.addEventListener('cart:update', refreshMounts);
    document.addEventListener('cart:lines-update', (event) => {
      if (event.promise && typeof event.promise.then === 'function') {
        event.promise.then(refreshMounts).catch(refreshMounts);
      } else {
        refreshMounts();
      }
    });
    document.addEventListener('dialog:open', initMounts);
    document.addEventListener('dialog:close', closePanel);
    const observer = new MutationObserver(() => initMounts());
    observer.observe(document.documentElement, { childList: true, subtree: true });
  }

  bindClicks();
  watchDrawer();
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initMounts, { once: true });
  } else {
    initMounts();
  }
})();
